import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { StatusChip } from '../components/ui';
import type { FraudReviewDonationItem, FraudReviewDonorDetail } from '../api/adminOps';
import {
  donorName,
  entryGapLabel,
  findingSentence,
  formatAccountAge,
  formatAmount,
  formatDay,
  isReceiptUrlExpired,
  reasonLabel,
  reasonsWithoutFindings,
  reasonTone,
  SEVERITY_PRESENTATION,
  truncatedCaseNotice,
} from './fraudReview.logic';

const SECTION_HEADING = 'text-xs font-semibold uppercase tracking-wider text-gray-text/70 mb-2';

interface ReceiptProps {
  donation: FraudReviewDonationItem;
  /** Asks for the case again, which re-signs every receipt URL. */
  onReceiptExpired: () => void;
}

/**
 * The URL is presigned for five minutes, so it is requested eagerly rather
 * than lazily: a lazy image below the fold could be fetched only after its
 * URL had lapsed. A reviewer can still keep a case open past the expiry, so a
 * failed load shows the fallback, and when the URL had expired it also
 * re-fetches the case for a fresh one (the new src clears the failure).
 */
function ReceiptThumbnail({ donation, onReceiptExpired }: ReceiptProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const frame = 'h-16 w-16 flex-shrink-0 rounded border border-subtle';
  const receipt = donation.receipt;
  if (receipt?.thumbnail_url && receipt.thumbnail_url !== failedSrc) {
    const src = receipt.thumbnail_url;
    return (
      <img
        src={src}
        alt={`Receipt for ${formatAmount(donation.amount_cents)}`}
        className={`${frame} object-cover`}
        onError={() => {
          setFailedSrc(src);
          if (isReceiptUrlExpired(receipt.expires_at, Date.now())) onReceiptExpired();
        }}
      />
    );
  }
  // A document whose thumbnail is not ready (or could not be signed) is still
  // evidence that a receipt exists; say which case this is.
  const text = donation.receipt ? 'Preview unavailable' : 'No receipt';
  return (
    <div
      className={`${frame} flex items-center justify-center p-1 text-center text-[10px] text-muted`}
    >
      {text}
    </div>
  );
}

function DonationEvidence({ donation, onReceiptExpired }: ReceiptProps) {
  const gap = entryGapLabel(donation);
  const uncaptured = reasonsWithoutFindings(donation.reasons, donation.findings);
  return (
    <li className="flex gap-3 rounded border border-subtle p-3">
      <ReceiptThumbnail donation={donation} onReceiptExpired={onReceiptExpired} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-medium tabular-nums">{formatAmount(donation.amount_cents)}</span>
          <span className="truncate text-xs text-muted">
            {donation.organization_name ?? donation.organization_id ?? 'No organization'}
          </span>
        </div>
        <div className="mt-1 text-xs text-muted">
          Given {formatDay(donation.donation_date)} · Recorded {formatDay(donation.created_at)}
          {gap !== null && <span className="ml-1">· {gap}</span>}
        </div>
        <ul className="mt-2 space-y-1">
          {donation.findings.map(finding => (
            <li key={finding.code} className="text-xs text-gray-text">
              {findingSentence(finding)}
            </li>
          ))}
        </ul>
        {uncaptured.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {uncaptured.map(reason => (
              <StatusChip key={reason} tone={reasonTone(reason)} label={reasonLabel(reason)} />
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

function DonorSummary({ detail }: { detail: FraudReviewDonorDetail }) {
  const severity = SEVERITY_PRESENTATION[detail.severity];
  const donor = detail.donor;
  return (
    <section aria-labelledby="fraud-case-donor" className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <h3 id="fraud-case-donor" className="font-medium text-white">
          {donor ? (donorName(donor) ?? 'Unnamed donor') : 'Account deleted'}
        </h3>
        <StatusChip tone={severity.tone} label={`${severity.label} severity`} />
      </div>
      <p className="text-xs text-muted break-all">{detail.user_id}</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {donor && (
          <>
            <dt className="text-muted">Account</dt>
            <dd>
              {formatAccountAge(donor.account_age_days)} · {donor.status}
            </dd>
          </>
        )}
        <dt className="text-muted">Lifetime donations</dt>
        <dd className="tabular-nums">{detail.lifetime_donation_count}</dd>
      </dl>
    </section>
  );
}

/**
 * DM-5802: everything a reviewer needs for one donor, on one screen. Sections
 * with nothing in them are omitted rather than rendered empty, so an empty
 * section never reads like a check that came back clear.
 */
export default function FraudReviewCaseView({
  detail,
  onReceiptExpired,
}: {
  detail: FraudReviewDonorDetail;
  onReceiptExpired: () => void;
}) {
  const receiptMatches = detail.receipt_matches ?? [];
  const truncationNotice = truncatedCaseNotice(
    detail.donations.length,
    detail.flagged_donation_count
  );
  return (
    <div className="space-y-5">
      <DonorSummary detail={detail} />

      {receiptMatches.length > 0 && (
        <section className="rounded border border-red-500/30 p-3">
          <h3 className="mb-1 text-sm font-medium text-red-400">
            Same receipt image as {new Set(receiptMatches.map(m => m.other_user_id)).size} other
            donor(s)
          </h3>
          <p className="mb-2 text-xs text-muted">
            Two accounts holding one receipt means at least one is claiming a deduction that is not
            theirs. Upload order is shown as evidence only — uploading first does not make a claim
            legitimate.
          </p>
          <ul className="space-y-2">
            {receiptMatches.map(match => (
              <li
                key={`${match.image_hash}-${match.other_user_id}`}
                className="text-xs text-gray-text"
              >
                <span className="font-medium">{match.other_user_id}</span> uploaded{' '}
                {formatDay(match.other_uploaded_at)}
                {match.uploaded_first === 'other_donor'
                  ? ' — before this donor'
                  : ' — after this donor'}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="fraud-case-donations">
        <h3 id="fraud-case-donations" className={SECTION_HEADING}>
          Flagged donations
        </h3>
        {truncationNotice && <p className="text-xs text-muted mb-2">{truncationNotice}</p>}
        {detail.donations.length === 0 ? (
          <p className="text-sm text-muted">No flagged donations remain for this donor.</p>
        ) : (
          <ol className="space-y-3">
            {detail.donations.map(donation => (
              <DonationEvidence
                key={donation.donation_id}
                donation={donation}
                onReceiptExpired={onReceiptExpired}
              />
            ))}
          </ol>
        )}
      </section>

      {detail.open_document_holds.length > 0 && (
        <section aria-labelledby="fraud-case-holds">
          <h3 id="fraud-case-holds" className={SECTION_HEADING}>
            Open document quarantines
          </h3>
          <ul className="space-y-1 text-xs text-gray-text">
            {detail.open_document_holds.map(hold => (
              <li key={hold.document_id}>
                Document {hold.document_id} · {hold.processing_status} · held since{' '}
                {formatDay(hold.created_at)}
              </li>
            ))}
          </ul>
        </section>
      )}

      {detail.prior_decisions.length > 0 && (
        <section aria-labelledby="fraud-case-decisions">
          <h3 id="fraud-case-decisions" className={SECTION_HEADING}>
            Previous decisions
          </h3>
          <ul className="space-y-2 text-xs">
            {detail.prior_decisions.map(decision => (
              <li key={`${decision.resolved_at}-${decision.disposition}`}>
                <span className="font-medium text-white">{decision.disposition}</span>{' '}
                <span className="text-gray-text">
                  · {formatDay(decision.resolved_at)} · {decision.donations_resolved} donation(s)
                  {decision.resolved_by ? ` · by ${decision.resolved_by}` : ''}
                </span>
                {decision.rationale && <p className="text-muted">{decision.rationale}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {detail.audit_timeline.length > 0 && (
        <section aria-labelledby="fraud-case-audit">
          <h3 id="fraud-case-audit" className={SECTION_HEADING}>
            Admin audit timeline
          </h3>
          <ul className="space-y-2 text-xs">
            {detail.audit_timeline.map(event => (
              <li key={event.id}>
                <span className="text-white">{event.action}</span>{' '}
                <span className="text-gray-text">
                  — {event.outcome}
                  {event.reason_code ? ` (${event.reason_code})` : ''} ·{' '}
                  {formatDistanceToNow(new Date(event.occurred_at), { addSuffix: true })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
